import type { FrictionPreset } from '../data/friction';
import type { TerrainLayout } from '../rules/terrain';

/** Spell hotkey slots, in shop/HUD order (GDD: Q W E R, then D F, then C V). */
export const SLOT_KEYS = ['Q', 'W', 'E', 'R', 'D', 'F', 'C', 'V'] as const;
export type SlotKey = (typeof SLOT_KEYS)[number];

export const SPELL_IDS = [
  'fireball',
  'homing',
  'lightning',
  'meteor',
  'teleport',
  'thrust',
  'shield',
  'link',
] as const;
export type SpellId = (typeof SPELL_IDS)[number];

/**
 * Spell runes (DROW-140/155): 2–3 per spell, one active per owned spell. Table order is the order
 * of `RUNES_BY_SPELL` and of the shop's rune row.
 */
export const RUNE_IDS = [
  'detonation',
  'searingBolt',
  'kindling',
  'seeker',
  'ambush',
  'splinter',
  'chain',
  'overcharge',
  'pierce',
  'comet',
  'cataclysm',
  'magmaPool',
  'blinkStrike',
  'recall',
  'quickstep',
  'batteringRam',
  'slipstream',
  'emberward',
  'mirror',
  'repulse',
  'reel',
  'grapple',
  'snap',
] as const;
export type RuneId = (typeof RUNE_IDS)[number];

/**
 * Playable characters. Each has a racial and may override base stats; the simulation reads
 * them only through `characterMods` (and not at all with the `racialAbilities` setting off).
 */
export const CHARACTER_IDS = ['warlock', 'mage', 'ranger', 'graniteborn', 'rogue'] as const;
export type CharacterId = (typeof CHARACTER_IDS)[number];

export const RACIAL_IDS = [
  'stoneGuard',
  'pactfire',
  'arcaneRecall',
  'fleetfoot',
  'stealth',
] as const;
export type RacialId = (typeof RACIAL_IDS)[number];

export const GamePhase = {
  Waiting: 'waiting',
  Shop: 'shop',
  Countdown: 'countdown',
  Fight: 'fight',
  RoundEnd: 'roundEnd',
  MatchEnd: 'matchEnd',
} as const;
export type GamePhase = (typeof GamePhase)[keyof typeof GamePhase];

/** Synced as uint8, so values are numeric. */
export const MoveState = {
  Idle: 0,
  Moving: 1,
  KnockedBack: 2,
  Frozen: 3,
  Dead: 4,
  /** Sliding under a Thrust dash; keeps this state until the dash slows to walking control. */
  Dashing: 5,
} as const;
export type MoveState = (typeof MoveState)[keyof typeof MoveState];

/** Bot difficulty tiers, easiest first. A seat's `botTier` is `''` for humans. */
export const BOT_TIERS = ['dummy', 'easy', 'normal', 'hard'] as const;
export type BotTier = (typeof BOT_TIERS)[number];

export const ARENA_SHAPES = ['circle', 'square'] as const;
export type ArenaShape = (typeof ARENA_SHAPES)[number];

export const GAME_MODES = ['ffa', 'teams'] as const;
export type GameMode = (typeof GAME_MODES)[number];

/** Playable maps, in canonical rotation order. */
export const MAP_IDS = [
  'emberRing',
  'frostmoatYard',
  'crucible',
  'standingStones',
  'frozenRuins',
  'verdantMire',
] as const;
export type MapId = (typeof MAP_IDS)[number];

export const MAP_SKINS = [
  'flagstone',
  'frostCobble',
  'scorchedDirt',
  'mossyRuin',
  'frozenRuin',
  'verdantMire',
  'calderaRock',
] as const;
export type MapSkin = (typeof MAP_SKINS)[number];

/** Secondary floor albedos a map's floor blends in (DROW-122); client-only, no gameplay effect. */
export const FLOOR_BLENDS = [
  'grass',
  'mudDirt',
  'snow',
  'ashDirt',
  'obsidianGrit',
  'iceSheet',
] as const;
export type FloorBlend = (typeof FLOOR_BLENDS)[number];

/** Host toggle for random map props: none, client-only decoration, or decoration + gameplay props. */
export const MAP_PROPS_MODES = ['off', 'decor', 'full'] as const;
export type MapPropsMode = (typeof MAP_PROPS_MODES)[number];

/**
 * Host obstacle density (DROW-138): scales how many blockers and hideouts `full` map props place.
 * `normal` is the map's own counts; placement rules never change.
 */
export const OBSTACLE_DENSITIES = ['sparse', 'normal', 'dense'] as const;
export type ObstacleDensity = (typeof OBSTACLE_DENSITIES)[number];

/** Generated blocker looks; the radius comes from `BLOCKERS`. Physics ignores the variant. */
export const BLOCKER_VARIANTS = ['stone', 'column', 'crystal'] as const;
export type BlockerVariant = (typeof BLOCKER_VARIANTS)[number];

/** Hideout cluster shapes (tuning in `HIDEOUTS`). */
export const HIDEOUT_SHAPES = ['wall', 'crescent'] as const;
export type HideoutShape = (typeof HIDEOUT_SHAPES)[number];

/** Hideout looks per map: ruin pieces, or crystal blockers per stone. */
export const HIDEOUT_LOOKS = ['ruin', 'crystal'] as const;
export type HideoutLook = (typeof HIDEOUT_LOOKS)[number];

/** Obstacle looks a hideout stone can have (`hideoutVariant`). */
export type HideoutVariant = 'ruinWall' | 'ruinCrescent' | 'crystal';

/** Cosmetic look of a generated map obstacle. Physics ignores it. */
export type ObstacleVariant = BlockerVariant | HideoutVariant;

/**
 * Surface zones: flat, non-colliding discs that change slide friction and walk speed (tuning in
 * `ZONES`). Magma Pools (`MAGMA_POOL_ZONE`) sync as zones too but are not a `ZoneKind`.
 */
export const ZONE_KINDS = ['frozenPool', 'mudPatch'] as const;
export type ZoneKind = (typeof ZONE_KINDS)[number];

/**
 * The synced `Zone.kind` of a Magma Pool (rune). Not a `ZoneKind`: the layout generator never
 * places it, and it has no surface effect (friction and walk speed as the map's).
 */
export const MAGMA_POOL_ZONE = 'magmaPool';

/** Client-only decoration kits (`DECOR_KITS`). */
export const DECOR_THEMES = [
  'flagstone',
  'frostCobble',
  'scorchedDirt',
  'mossyRuin',
  'frozenRuin',
  'caldera',
] as const;
export type DecorTheme = (typeof DECOR_THEMES)[number];

/** `rotate`: each round loads the next map's preset. `fixed`: every round uses the host's arena. */
export const MAP_MODES = ['rotate', 'fixed'] as const;
export type MapMode = (typeof MAP_MODES)[number];

/** The minimum a physics body needs; server schema and client prediction both satisfy it. */
export interface PlayerBody {
  x: number;
  y: number;
  /** Slide (knockback) velocity in units/s. Walking is not stored here. */
  vx: number;
  vy: number;
  /** A MoveState value; typed as number so synced uint8 schema fields satisfy it. */
  moveState: number;
}

/** `map`: a map pillar from the preset. `spell`: spawned by a spell, with a lifetime. */
export const OBSTACLE_KINDS = ['map', 'spell'] as const;
export type ObstacleKind = (typeof OBSTACLE_KINDS)[number];

/** A static circular obstacle as physics sees it. Timers live only on the synced schema. */
export interface ObstacleBody {
  /** Monotonic per room, never 0 (0 means "no obstacle", e.g. in `tetherObstacle`). */
  id: number;
  x: number;
  y: number;
  r: number;
}

export interface Obstacle extends ObstacleBody {
  kind: ObstacleKind;
  /** Cosmetic look of a generated map blocker or hideout stone; absent = the default pillar. */
  variant?: ObstacleVariant;
  /** Hideout stones: the id of the cluster's first stone, shared by the cluster; absent/0 = none. */
  groupId?: number;
}

/**
 * A surface zone as physics sees it. `frictionMult` is the effective slide friction inside it;
 * `moveSpeedMult` scales walking speed inside it (1 = unchanged).
 */
export interface SurfaceZoneBody {
  id: number;
  x: number;
  y: number;
  r: number;
  frictionMult: number;
  moveSpeedMult: number;
  /**
   * Mud (`ZONES[kind].endsOnLava`): no effect on a body whose centre is on lava, melt warning
   * included (AC11). Absent = the zone works on lava too (frozen pools).
   */
  endsOnLava?: boolean;
}

/**
 * Continuous movement intent, sent on every input frame. Humans hold a direction (`moveDir`, WASD);
 * bots walk to a point target. When both are set the direction wins.
 */
export interface MoveIntent {
  hasMoveTarget: boolean;
  moveX: number;
  moveY: number;
  /** `MoveDir` bit flags of the held directions; absent/0 = none (bots, point targets). */
  moveDir?: number;
}

export interface SpellSlot {
  spellId: SpellId;
  level: number;
  /** Server clock ms; 0 = ready. */
  cooldownEndsAt: number;
  /** The active RuneId; '' = none. Public: runes are visible to opponents. */
  rune?: string;
  /** Unlocked RuneIds for this spell, in unlock order. */
  unlocked?: Iterable<string>;
}

export interface Player extends PlayerBody {
  sessionId: string;
  name: string;
  colorIndex: number;
  /** A CharacterId; a plain string so the synced schema field satisfies it. See `characterMods`. */
  character: string;
  /** 0 = free-for-all. */
  team: number;
  facing: number;
  hp: number;
  maxHp: number;
  alive: boolean;
  ready: boolean;
  connected: boolean;
  gold: number;
  points: number;
  kills: number;
  deaths: number;
  /** Damage dealt to other warlocks this match (scoreboard). */
  damageDealt: number;
  /** Obstacle id a Link hook is pulling this player to; 0 = none. */
  tetherObstacle: number;
  /** Warlock sessionId a Link hook is pulling this player to; '' = none. */
  tetherTargetId: string;
  /** Server clock ms when the tether breaks. */
  tetherEndsAt: number;
  /** Speed (units/s) of this player's tether pull (the hook's rune); 0 = the base Link pull. */
  tetherPullSpeed?: number;
  /** Server clock ms until which Shield reflects projectiles and Thrust; 0 = no shield. */
  shieldUntil: number;
  /** Free casts left this round (Pactfire): casts from a `freeCastsPerRound` slot skip the cooldown. */
  racialCharges: number;
  /** Server clock ms until which Emberward is up (lava immunity, knockback resist); 0 = none. */
  wardUntil?: number;
  /** Server clock ms until which Stealth is up (Rogue); 0 = not stealthed. */
  stealthUntil?: number;
  /** Server clock ms until which a Kindling burn ticks; 0 = not burning. */
  burnUntil?: number;
  /** Recall: the blink's origin, and until when a second press returns there (0 = no window). */
  recallX?: number;
  recallY?: number;
  recallUntil?: number;
  /** Voted to end the shop early; reset on every Shop entry. */
  shopReady: boolean;
}

export interface Projectile {
  id: string;
  ownerId: string;
  spellId: SpellId;
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Spell level it was cast at. */
  level: number;
  /** Server clock ms. */
  spawnedAt: number;
  /** Server clock ms when it runs out of range. */
  expiresAt: number;
  /** Homing: the warlock it is chasing; '' until one is detected. */
  targetId: string;
  /** The caster's active RuneId at cast; kept through reflects. '' = none. */
  rune?: string;
  /** Detonation: the ground target it explodes at. */
  tx?: number;
  ty?: number;
  /** A Splinter fragment (non-homing, spawned by its orb). */
  fragment?: boolean;
}

/** A telegraphed area impact (Meteor) waiting to land. */
export interface PendingArea {
  id: string;
  ownerId: string;
  spellId: SpellId;
  /** Spell level it was cast at. */
  level: number;
  x: number;
  y: number;
  /** Impact radius. */
  r: number;
  /** Server clock ms when it was cast (telegraph start). */
  castAt: number;
  /** Server clock ms when it lands. */
  landsAt: number;
  /** The caster's active RuneId at cast (Comet, Cataclysm, Magma Pool); '' = none. */
  rune?: string;
}

export interface Arena {
  shape: ArenaShape;
  /** Current safe radius (half-width for the square shape). */
  radius: number;
}

export type SpellKind = 'projectile' | 'instant' | 'area' | 'blink' | 'dash' | 'self' | 'hook';

/**
 * Stat change per upgrade level beyond 1: level L adds (L − 1) × delta to the base value.
 * Damage, force, range and duration only grow; cooldown only shrinks.
 */
export interface SpellLevelDelta {
  damage?: number;
  force?: number;
  cooldownMs?: number;
  range?: number;
  durationMs?: number;
}

export interface SpellDef {
  id: SpellId;
  name: string;
  slot: SlotKey;
  kind: SpellKind;
  /** Purchase price in gold; 0 = owned by everyone at start. */
  cost: number;
  upgradeCost: number;
  maxLevel: number;
  cooldownMs: number;
  damage: number;
  /** Base knockback force (units/s of slide velocity), F_base in the GDD formula. */
  force: number;
  range: number;
  /** Projectile speed in units/s (projectile/hook kinds). */
  speed?: number;
  /** Hit radius for projectiles, area radius for area spells. */
  radius?: number;
  /** Homing: a warlock within this distance of the projectile becomes its target. */
  detectRadius?: number;
  /** Homing: maximum turn rate toward the target, in radians/s. */
  turnRate?: number;
  /** Delay before an area impact lands. */
  delayMs?: number;
  /** Active duration for self buffs, and the tether cap for hooks. */
  durationMs?: number;
  /** Hook: speed (units/s) at which a tether pulls the caster in. */
  pullSpeed?: number;
  /**
   * Stones stop it (O5): a projectile or hook is destroyed on a stone, Lightning stops at the
   * first stone, Thrust ends at the contact point, and an area impact spares enemies with a
   * stone between them and its centre.
   */
  blockedByStones: boolean;
  /**
   * Stone Guard reduces its hits from inside the guard arc. Spells that never hit (Teleport,
   * Shield) are false; future non-hit effects (pulls, swaps) opt out here.
   */
  guardable: boolean;
  /** Per-level upgrade deltas; see `spellStats`. */
  perLevel: SpellLevelDelta;
}

/** Level-1 stat fields a rune may replace (`spellStats` applies them before the level deltas). */
export type RuneOverrides = Partial<
  Pick<
    SpellDef,
    | 'cooldownMs'
    | 'damage'
    | 'force'
    | 'range'
    | 'speed'
    | 'radius'
    | 'detectRadius'
    | 'turnRate'
    | 'delayMs'
    | 'durationMs'
    | 'pullSpeed'
  >
>;

/**
 * What a rune does beyond stat overrides, by tag. The server sim implements each tag
 * (`systems/runes.ts`); params don't scale with the spell level. Knockback directions are the
 * spec's: blasts push away from their centre.
 */
export type RuneBehaviour =
  /** Ground-target: flies to the clicked point and explodes there; passes through warlocks. */
  | { tag: 'detonateAtTarget'; blastRadius: number }
  /** The hit sets the target burning: `dps` per second for `durationMs`, no knockback. */
  | { tag: 'burn'; dps: number; durationMs: number }
  /** Flies straight (no homing) for `straightMs`, then speeds up to `boostSpeed` and homes. */
  | { tag: 'delayedHoming'; straightMs: number; boostSpeed: number }
  /** On a hit or at max range: `count` non-homing fragments in a `fanDeg` forward fan. */
  | {
      tag: 'splitOnEnd';
      count: number;
      fanDeg: number;
      damage: number;
      force: number;
      radius: number;
      range: number;
      speed: number;
    }
  /** After a hit, one jump to the nearest other enemy within `jumpRange` of the struck warlock. */
  | { tag: 'chain'; jumpRange: number; damage: number; force: number }
  /** A telegraphed windup: the aim locks at cast, the bolt fires `windupMs` later. */
  | { tag: 'windup'; windupMs: number }
  /** Hits every warlock on the line (still stops at the first stone). */
  | { tag: 'pierce' }
  /** The impact leaves a lava pool (enemies of the caster only). */
  | { tag: 'lavaPool'; radius: number; durationMs: number }
  /** A shockwave around the landing point. */
  | { tag: 'arrivalBlast'; radius: number; damage: number; force: number }
  /** For `windowMs` after the blink, pressing the slot again returns to the origin. */
  | { tag: 'recall'; windowMs: number }
  /** The dash stops at the first warlock; the caster takes `recoilFrac` of the impulse back. */
  | { tag: 'stopAtFirst'; recoilFrac: number }
  /** No warlock collision or hit; stones still stop it. */
  | { tag: 'phaseThrough' }
  /** No reflect; lava immunity and `kbResist` knockback resist while up. */
  | { tag: 'lavaWard'; kbResist: number }
  /** Reflected projectiles gain speed and force (once per projectile); a reflected Thrust force. */
  | { tag: 'empoweredReflect'; speedMult: number; forceMult: number }
  /** When the shield ends, a nova pushes enemies within `radius` away. */
  | { tag: 'endNova'; radius: number; damage: number; force: number }
  /** The tether pulls the target to the caster; a stone hook does nothing. */
  | { tag: 'reverseTether' }
  /** Reaching the hooked warlock knocks it along the travel direction. */
  | { tag: 'arrivalKnock'; force: number };

export type RuneTag = RuneBehaviour['tag'];

export interface RuneDef {
  id: RuneId;
  spellId: SpellId;
  name: string;
  /** One line for the shop tooltip and HUD. */
  blurb: string;
  /** Unlock price in gold. */
  cost: number;
  overrides: RuneOverrides;
  /** Replaces the spell's `perLevel` when present. */
  perLevel?: SpellLevelDelta;
  /** null = a data-only rune (its overrides are the whole effect). */
  behaviour: RuneBehaviour | null;
}

export interface CharacterDef {
  id: CharacterId;
  displayName: string;
  blurb: string;
  /** Model file: /characters/{modelKey}.glb (mask: {modelKey}.mask.png). */
  modelKey: string;
  /** Portrait file: /portraits/{iconKey}.webp. */
  iconKey: string;
  /** Disabled entries stay in the table (old clients and ids keep parsing) but can't be picked. */
  enabled: boolean;
  /** null = no racial (cosmetic-only character). */
  racial: RacialId | null;
  /**
   * Base stats. Omitted fields = PLAYER_BASE_HP / PLAYER_MOVE_SPEED. The hitbox is
   * never overridden: a bigger or smaller body would be an invisible stat.
   */
  stats?: CharacterStats;
}

export interface CharacterStats {
  maxHp?: number;
  moveSpeed?: number;
}

/** Movement changes by surface. Read by stepPlayer on the server, in client prediction and bots. */
export interface SurfaceMods {
  /** Walk speed multiplier on stone (Fleetfoot). */
  stoneSpeedMult?: number;
}

interface RacialBase {
  id: RacialId;
  name: string;
  blurb: string;
}

/** Always-on racial; its effect comes from the optional modifier fields. */
export interface PassiveRacialDef extends RacialBase {
  kind: 'passive';
  surface?: SurfaceMods;
  /** Spell cooldown multiplier by slot (Arcane Recall: { D: 0.9 }). */
  cooldownMult?: Partial<Record<SlotKey, number>>;
  /** Casts per round that skip the cooldown (Pactfire: { Q: 1 }). */
  freeCastsPerRound?: Partial<Record<SlotKey, number>>;
}

/**
 * What an `active` racial does on Space. `stealth` (Rogue): vanish for `durationMs`; enemies get
 * no position at any distance.
 */
export type ActiveRacialEffect = 'stealth';

/** Active (Space) racial with a cooldown (Stealth). */
export interface ActiveRacialDef extends RacialBase {
  kind: 'active';
  effect: ActiveRacialEffect;
  cooldownMs: number;
  /** How long it lasts (`stealth`). */
  durationMs?: number;
}

/** Active (T) racial: a directional brace that softens hits from inside its arc. */
export interface GuardRacialDef extends RacialBase {
  kind: 'guard';
  cooldownMs: number;
  durationMs: number;
  /** Full arc width in degrees, centred on the facing at cast. */
  arcDeg: number;
  /** Knockback multiplier on covered hits, applied after the resist cap. */
  kbMult: number;
  /** Damage multiplier on covered hits. */
  damageMult: number;
  /** Walk speed multiplier while guarding. */
  moveSpeedMult: number;
}

export type RacialDef = PassiveRacialDef | GuardRacialDef | ActiveRacialDef;

export interface MatchSettings {
  mode: GameMode;
  rounds: number;
  startingGold: number;
  goldPerRound: number;
  shopSeconds: number;
  arenaShape: ArenaShape;
  /** Starting radius for an 8-player match; scaled by player count at round start. */
  startRadius: number;
  graceMs: number;
  shrinkIntervalMs: number;
  /** Fraction of the starting radius lost per shrink step. */
  shrinkPct: number;
  /** Fraction of max HP lost per second on lava. */
  lavaDpsPct: number;
  /** Lobby slide-friction preset; scales each map's surface friction. Walking is unaffected. */
  frictionPreset: FrictionPreset;
  knockbackMult: number;
  mapMode: MapMode;
  /** Maps in the rotation; never empty. Ignored in `fixed` mode. */
  mapPool: MapId[];
  /** Round 1's map in `rotate` mode; the only map in `fixed` mode. Always in `mapPool`. */
  startMapId: MapId;
  /** Random map props: `off` = today's maps, `decor` = decoration only, `full` = gameplay props too. */
  mapProps: MapPropsMode;
  /** Character racials and stat overrides; off = every character plays the baseline. */
  racialAbilities: boolean;
  /** Blocker and hideout counts for `full` map props; per match, locked once it starts. */
  obstacleDensity: ObstacleDensity;
}

/** Arena constants the host can edit in `fixed` mode (a map preset's otherwise). */
export type HostArena = Pick<
  MatchSettings,
  'arenaShape' | 'startRadius' | 'graceMs' | 'shrinkIntervalMs' | 'shrinkPct' | 'lavaDpsPct'
>;

/** The arena constants a map controls. */
export type MapArena = HostArena & {
  /**
   * Slide (knockback) friction; < 1 slides farther. On a map preset this is the surface value;
   * on a RoundArena it is the effective value (surface × lobby preset, clamped).
   */
  frictionMult: number;
};

/**
 * A map pillar, placed at `angleDeg` and `distFrac` × the round's player-scaled start radius.
 * `r` is a hitbox and does not scale.
 */
export interface MapObstacleDef {
  angleDeg: number;
  distFrac: number;
  r: number;
  /** Only placed when at least this many players start the round. */
  minPlayers?: number;
}

/** Per player band at round start: P 2–3, 4–6, 7–9, 10–12 (see `propBand`). */
export type BandCounts = readonly [number, number, number, number];

/** Random props for a map. Counts are maximums; the generator leaves out what it can't fit. */
export interface MapPropsProfile {
  blockers?: { variants: readonly BlockerVariant[]; counts: BandCounts };
  /**
   * Surface zones per kind, generated in array order on one RNG stream (so a single-kind map's
   * layout matches the pre-array form). Counts are per kind.
   */
  zones?: readonly { kind: ZoneKind; counts: BandCounts }[];
  /**
   * Centre hideouts (DROW-122); absent = none. `counts` / `mirrorCounts` / `gap` override
   * `HIDEOUTS.counts` / `HIDEOUTS.mirrorCounts` (the latter for point-symmetric Teams layouts) /
   * `HIDEOUTS.gap` (for maps whose fixed pillars crowd the centre band).
   */
  hideouts?: {
    look: HideoutLook;
    counts?: BandCounts;
    mirrorCounts?: BandCounts;
    gap?: number;
  };
  decor: { theme: DecorTheme; counts: BandCounts };
}

export interface MapPreset {
  id: MapId;
  name: string;
  /** One-line gameplay hint for the shop's next-map banner. */
  hint: string;
  skin: MapSkin;
  arena: MapArena;
  /** Map pillars; absent means an open floor. Fixed per map (hosts can't edit them). */
  obstacles?: readonly MapObstacleDef[];
  /** Random props (DROW-109), generated per round by `generateLayout`/`generateDecor`. */
  props?: MapPropsProfile;
  /**
   * Cosmetic floor blend (DROW-122): up to 2 textures mixed over the skin in `decor`/`full`, by a
   * seeded noise mask (`noise`, the default) or as three caldera wedges (`sectors`, DROW-188:
   * layer 0 ice, layer 1 grass, the skin rock). Client-only; the server never reads it.
   */
  terrain?: { blend: readonly FloorBlend[]; layout?: TerrainLayout };
}

/** The arena actually in play for one round. */
export interface RoundArena extends MapArena {
  mapId: MapId;
  skin: MapSkin;
}
